import axios from "axios";
import { gql, useQuery } from "@apollo/client";
import { useCallback, useContext, useMemo } from "react";
import { AlertContext, CurrentUserContext, ReflectDataContext, RoleOverrideContext, TokenContext } from './App.provider';
import { apiUri } from "./authConfig";
import { UserRoleEnum } from './generated/graphql';
import { hasPreviewFeature, PreviewFeatureEntry, PreviewFeatureKey } from './previewFeatures';

const GET_MY_PREVIEW_FEATURES = gql`
    query getMyPreviewFeatures {
        Me {
            dynamicData {
                id
                type
                ownerId
                sharedById
            }
        }
    }
`;

export const useSuccessAlert = () => {
    const [setopen, setmessage] = useContext(AlertContext);
    const [reflectData, setreflectDatra] = useContext(ReflectDataContext);
    const setSuccessAlert = (message: string) => {
        setmessage(message);
        setopen(true);
        setreflectDatra(!reflectData);
    };
    return setSuccessAlert;
};

/**
 * Returns the current user, with role overridden if "View as User" is active.
 * The override only affects the client-side UI — API calls still use the real identity.
 */
export const useCurrentUser = () => {
    const user = useContext(CurrentUserContext);
    const [roleOverride] = useContext(RoleOverrideContext);

    return useMemo(() => {
        if (!user || !roleOverride) return user;
        return { ...user, role: roleOverride };
    }, [user, roleOverride]);
};

/** Returns the real (un-overridden) user — use when you need the actual identity. */
export const useRealUser = () => {
    return useContext(CurrentUserContext);
};

export const usePreviewFeatures = () => {
    const realUser = useRealUser();
    const { data, loading } = useQuery<{
        Me?: { dynamicData?: Array<PreviewFeatureEntry | null> | null } | null;
    }>(GET_MY_PREVIEW_FEATURES, {
        skip: !realUser?.id,
    });

    return {
        hasFeature: (key: PreviewFeatureKey) => hasPreviewFeature(data?.Me?.dynamicData, realUser?.id, key),
        loading,
    };
};

/** Returns true if the current user's email is outside the @aurecongroup.com domain. */
export const useIsExternalUser = () => {
    const user = useContext(CurrentUserContext);
    return useMemo(() => {
        if (!user?.email) return false;
        return !user.email.toLowerCase().endsWith('@aurecongroup.com');
    }, [user]);
};

/** Toggle between viewing the app as Admin vs a regular user. Only available to real admins. */
export const useRoleOverride = () => {
    const realUser = useContext(CurrentUserContext);
    const [roleOverride, setRoleOverride] = useContext(RoleOverrideContext);

    const isRealAdmin = realUser?.role === UserRoleEnum.Admin;
    const isOverrideActive = roleOverride !== null;

    const toggleOverride = () => {
        if (!isRealAdmin) return;
        setRoleOverride(isOverrideActive ? null : UserRoleEnum.None);
    };

    return { isRealAdmin, isOverrideActive, toggleOverride };
};

export const useAccessToken = () => {
    const token = useContext(TokenContext);
    return token;
};

export const useLoadHttpClientEmpty = () => {
    const token = useAccessToken();

    return useMemo(() => {
        const backend = axios.create();
        return backend;
    }, [token]);
};

export const useLoadHttpClient = () => {
    const token = useAccessToken();

    return useMemo(() => {
        const backend = axios.create({
            baseURL: apiUri,
            headers: {
                Authorization: token && `Bearer ${token}`,
                "Cache-Control": "no-cache"
            }
        });
        return backend;
    }, [token]);
};

export function useDownloadAttachment(): (attachmentId: string) => Promise<string> {
    const client = useLoadHttpClientEmpty();

    const handleDownload = useCallback(async (attachmentId: string) => {
        // SAS/CDN URLs are aggressively cached. Append a unique cache-buster
        // and send no-cache headers so a freshly saved blob is never stale.
        const sep = attachmentId.includes("?") ? "&" : "?";
        const url = `${attachmentId}${sep}_cb=${Date.now()}`;
        const res = await client.get(url, {
            responseType: "text",
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                "Pragma": "no-cache"
            }
        });
        return res.data;
    }, [client]);

    return handleDownload;
}

export type DownloadProgress = {
    loaded: number;
    total: number | undefined;
    percent: number | undefined;
};

export function useDownloadAttachmentWithProgress(): (
    attachmentId: string,
    onProgress?: (progress: DownloadProgress) => void
) => Promise<string> {
    const client = useLoadHttpClientEmpty();

    const handleDownload = useCallback(async (
        attachmentId: string,
        onProgress?: (progress: DownloadProgress) => void
    ) => {
        const sep = attachmentId.includes("?") ? "&" : "?";
        const url = `${attachmentId}${sep}_cb=${Date.now()}`;
        const res = await client.get(url, {
            responseType: "text",
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                "Pragma": "no-cache"
            },
            onDownloadProgress: (progressEvent) => {
                if (onProgress) {
                    const total = progressEvent.total;
                    const loaded = progressEvent.loaded;
                    const percent = total ? Math.round((loaded / total) * 100) : undefined;
                    onProgress({ loaded, total, percent });
                }
            }
        });
        return res.data;
    }, [client]);

    return handleDownload;
}

export function useDownloadAttachmentBytesWithProgress(): (
    attachmentId: string,
    onProgress?: (progress: DownloadProgress) => void
) => Promise<ArrayBuffer> {
    const client = useLoadHttpClientEmpty();

    const handleDownload = useCallback(async (
        attachmentId: string,
        onProgress?: (progress: DownloadProgress) => void
    ) => {
        const sep = attachmentId.includes("?") ? "&" : "?";
        const url = `${attachmentId}${sep}_cb=${Date.now()}`;
        const res = await client.get<ArrayBuffer>(url, {
            responseType: "arraybuffer",
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                "Pragma": "no-cache"
            },
            onDownloadProgress: (progressEvent) => {
                if (onProgress) {
                    const total = progressEvent.total;
                    const loaded = progressEvent.loaded;
                    const percent = total ? Math.round((loaded / total) * 100) : undefined;
                    onProgress({ loaded, total, percent });
                }
            }
        });
        return res.data;
    }, [client]);

    return handleDownload;
}

